import { tokens } from '@/design';
import {
  effectiveScaleBarDenominator,
  scaleBarElementLayout,
  type ScaleBarGeometry,
} from './scaleBarGeometry';
import { scaleBarStyleMeta } from './scaleBarStyles';
import {
  northArrowRotationForElement,
  northArrowVisibleAtScale,
} from './northArrowOrientation';
import { legendItemsForLayers } from './legendLayerItems';
import {
  DEFAULT_LAYOUT_TEXT_SYMBOL,
  DEFAULT_SCALE_BAR_TEXT_SYMBOL,
} from './layoutDocument';
import { legendStyleMeta } from './legendStyles';
import { ptToMm } from './pageUnits';
import { renderLayoutGraphicToCanvas } from './renderLayoutGraphicToCanvas';
import type {
  LayoutDocument,
  LegendElement,
  LegendLayerItem,
  LegendTextPart,
  ScaleBarTextSymbol,
  TableFrameRuntimeData,
} from './layoutTypes';

export interface ExportLayoutOptions {
  readonly mapCanvas: HTMLCanvasElement | null;
  readonly legendLayers: readonly LegendLayerItem[];
  readonly visibleLayerIds: ReadonlySet<string>;
  readonly tableDataByLayer?: Readonly<Record<string, TableFrameRuntimeData>>;
  readonly dpi?: number;
}

function legendTextSymbol(
  legend: LegendElement,
  part: LegendTextPart,
): ScaleBarTextSymbol {
  const meta = legendStyleMeta(legend.styleId ?? 'centerTitle');
  const fallbackSize =
    part === 'title'
      ? meta.titleSizePt
      : part === 'description'
        ? Math.max(6, meta.textSizePt - 1)
        : meta.textSizePt;
  return {
    ...DEFAULT_LAYOUT_TEXT_SYMBOL,
    ...(legend.textSymbols?.[part] ?? {}),
    sizePt: legend.textSymbols?.[part]?.sizePt ?? fallbackSize,
    halo: {
      ...DEFAULT_LAYOUT_TEXT_SYMBOL.halo,
      ...legend.textSymbols?.[part]?.halo,
    },
    shadow: {
      ...DEFAULT_LAYOUT_TEXT_SYMBOL.shadow,
      ...legend.textSymbols?.[part]?.shadow,
    },
    callout: {
      ...DEFAULT_LAYOUT_TEXT_SYMBOL.callout,
      ...legend.textSymbols?.[part]?.callout,
    },
  };
}

function drawScaleText(
  ctx: CanvasRenderingContext2D,
  value: string,
  x: number,
  y: number,
  symbol: ScaleBarTextSymbol,
  dpi: number,
  defaultAlign: CanvasTextAlign,
): void {
  const pxPerPt = dpi / 72;
  const text =
    symbol.textCase === 'uppercase' || symbol.textCase === 'smallCaps'
      ? value.toUpperCase()
      : symbol.textCase === 'lowercase'
        ? value.toLowerCase()
        : value;
  const bold = symbol.fontStyle === 'bold' || symbol.fontStyle === 'boldItalic';
  const italic =
    symbol.fontStyle === 'italic' || symbol.fontStyle === 'boldItalic';
  ctx.save();
  ctx.globalAlpha = symbol.opacityPercent / 100;
  ctx.font = `${italic ? 'italic ' : ''}${bold ? '700 ' : ''}${Math.max(
    7,
    symbol.sizePt * pxPerPt,
  )}px ${symbol.fontFamily}`;
  ctx.textAlign =
    symbol.horizontalAlignment === 'center'
      ? 'center'
      : symbol.horizontalAlignment === 'right'
        ? 'right'
        : symbol.horizontalAlignment === 'justify'
          ? 'center'
          : defaultAlign;
  ctx.textBaseline =
    symbol.verticalAlignment === 'top'
      ? 'top'
      : symbol.verticalAlignment === 'middle'
        ? 'middle'
        : symbol.verticalAlignment === 'bottom'
          ? 'bottom'
          : 'alphabetic';
  const adjustment =
    symbol.positionAdjustment === 'superscript'
      ? -symbol.sizePt * 0.35
      : symbol.positionAdjustment === 'subscript'
        ? symbol.sizePt * 0.25
        : 0;
  const drawX = x + symbol.offsetXPt * pxPerPt;
  const drawY = y + (symbol.offsetYPt + adjustment) * pxPerPt;
  ctx.translate(drawX, drawY);
  ctx.rotate((symbol.angle * Math.PI) / 180);
  const metrics = ctx.measureText(text);
  const width = metrics.width;
  const left =
    ctx.textAlign === 'center' ? -width / 2 : ctx.textAlign === 'right' ? -width : 0;
  const top = -symbol.sizePt * pxPerPt;
  if (symbol.callout.enabled) {
    const padding = symbol.callout.paddingPt * pxPerPt;
    ctx.fillStyle = symbol.callout.fillColor;
    ctx.strokeStyle = symbol.callout.outlineColor;
    ctx.lineWidth = Math.max(1, symbol.outlineWidthPt * pxPerPt);
    ctx.fillRect(
      left - padding,
      top - padding,
      width + padding * 2,
      symbol.sizePt * pxPerPt * 1.35 + padding * 2,
    );
    ctx.strokeRect(
      left - padding,
      top - padding,
      width + padding * 2,
      symbol.sizePt * pxPerPt * 1.35 + padding * 2,
    );
  }
  if (symbol.shadow.enabled) {
    ctx.shadowColor = symbol.shadow.color;
    ctx.shadowOffsetX = symbol.shadow.offsetXPt * pxPerPt;
    ctx.shadowOffsetY = symbol.shadow.offsetYPt * pxPerPt;
  }
  if (symbol.halo.enabled) {
    ctx.strokeStyle = symbol.halo.color;
    ctx.lineJoin = 'round';
    ctx.lineWidth = symbol.halo.sizePt * pxPerPt * 2;
    ctx.strokeText(text, 0, 0);
  }
  if (symbol.outlineWidthPt > 0) {
    ctx.strokeStyle = symbol.outlineColor;
    ctx.lineJoin = 'round';
    ctx.lineWidth = symbol.outlineWidthPt * pxPerPt * 2;
    ctx.strokeText(text, 0, 0);
  }
  ctx.fillStyle = symbol.color;
  ctx.fillText(text, 0, 0);
  ctx.shadowColor = 'transparent';
  if (symbol.underline || symbol.strikethrough) {
    ctx.strokeStyle = symbol.color;
    ctx.lineWidth = Math.max(1, symbol.sizePt * pxPerPt * 0.06);
    if (symbol.underline) {
      ctx.beginPath();
      ctx.moveTo(left, symbol.sizePt * pxPerPt * 0.12);
      ctx.lineTo(left + width, symbol.sizePt * pxPerPt * 0.12);
      ctx.stroke();
    }
    if (symbol.strikethrough) {
      ctx.beginPath();
      ctx.moveTo(left, -symbol.sizePt * pxPerPt * 0.3);
      ctx.lineTo(left + width, -symbol.sizePt * pxPerPt * 0.3);
      ctx.stroke();
    }
  }
  ctx.restore();
}

function drawScaleBarRow(
  ctx: CanvasRenderingContext2D,
  geometry: ScaleBarGeometry,
  family: 'line' | 'bar',
  x: number,
  rowY: number,
  barHeight: number,
  mm: (value: number) => number,
  textSymbol: ScaleBarTextSymbol,
  dpi: number,
  labelsAbove: boolean,
): void {
  const barWidth = mm(geometry.widthMm);
  const step = barWidth / geometry.divisions;
  ctx.fillStyle = tokens.color.ink.inverse;
  ctx.strokeStyle = tokens.color.ink.inverse;
  ctx.lineWidth = Math.max(1, barHeight * 0.08);
  if (family === 'line') {
    ctx.beginPath();
    ctx.moveTo(x, rowY + barHeight);
    ctx.lineTo(x + barWidth, rowY + barHeight);
    for (let index = 0; index <= geometry.divisions; index += 1) {
      const tickX = x + index * step;
      ctx.moveTo(tickX, rowY);
      ctx.lineTo(tickX, rowY + barHeight * 1.7);
      if (index < geometry.divisions && geometry.subdivisions > 1) {
        for (
          let subdivision = 1;
          subdivision < geometry.subdivisions;
          subdivision += 1
        ) {
          const subX = tickX + (subdivision / geometry.subdivisions) * step;
          ctx.moveTo(subX, rowY + barHeight * 0.55);
          ctx.lineTo(subX, rowY + barHeight * 1.45);
        }
      }
    }
    ctx.stroke();
  } else {
    for (let index = 0; index < geometry.divisions; index += 1) {
      const boxX = x + index * step;
      if (index % 2 === 0) ctx.fillRect(boxX, rowY, step, barHeight);
      ctx.strokeRect(boxX, rowY, step, barHeight);
    }
  }

  geometry.divisionLabels.forEach((label, index) => {
    drawScaleText(
      ctx,
      label,
      x + index * step,
      labelsAbove ? rowY - barHeight * 0.35 : rowY + barHeight * 2.7,
      textSymbol,
      dpi,
      index === 0 ? 'left' : 'center',
    );
  });
  drawScaleText(
    ctx,
    geometry.unitLabel,
    x + barWidth + mm(1.2),
    rowY + barHeight,
    textSymbol,
    dpi,
    'left',
  );
}

function loadExportPicture(dataUrl: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    let settled = false;
    const done = (value: HTMLImageElement | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    image.onload = () => {
      done(image.naturalWidth > 0 || image.width > 0 ? image : null);
    };
    image.onerror = () => done(null);
    image.src = dataUrl;
    if (image.complete) {
      done(image.naturalWidth > 0 || image.width > 0 ? image : null);
    }
  });
}

async function rasterizeLayout(
  layout: LayoutDocument,
  options: ExportLayoutOptions,
): Promise<HTMLCanvasElement | null> {
  const dpi = options.dpi ?? 150;
  const scale = dpi / 25.4;
  const canvas = window.document.createElement('canvas');
  canvas.width = Math.round(layout.page.widthMm * scale);
  canvas.height = Math.round(layout.page.heightMm * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = tokens.color.canvas.light;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const pictures = new Map<string, HTMLImageElement>();
  await Promise.all(
    layout.elements.map(async (element) => {
      if (element.type !== 'picture' || !element.visible) return;
      const image = await loadExportPicture(element.source.dataUrl);
      if (image) pictures.set(element.id, image);
    }),
  );

  const mm = (value: number) => value * scale;

  for (const el of layout.elements) {
    const x = mm(el.rect.x);
    const y = mm(el.rect.y);
    const w = mm(el.rect.width);
    const h = mm(el.rect.height);
    if (el.type === 'mapFrame' && options.mapCanvas && options.mapCanvas.width > 0) {
      ctx.drawImage(options.mapCanvas, x, y, w, h);
      continue;
    }
    if (el.type === 'northArrow') {
      const frame = layout.elements.find(
        (item) => item.type === 'mapFrame' && item.id === el.mapFrameId,
      );
      if (
        !northArrowVisibleAtScale(
          el,
          frame && frame.type === 'mapFrame'
            ? frame.camera.scaleDenominator
            : null,
        )
      ) continue;
      ctx.save();
      ctx.translate(x + w / 2, y + h / 2);
      const rotation = frame && frame.type === 'mapFrame'
        ? northArrowRotationForElement(el, frame.camera)
        : -el.calibrationAngle;
      ctx.rotate((rotation * Math.PI) / 180);
      const symbol = el.symbol;
      // "No color" keeps the halo and drops the fill, as on the page.
      const hasFill = !symbol || symbol.color !== null;
      const ink = symbol?.color ?? tokens.color.ink.inverse;
      ctx.beginPath();
      ctx.moveTo(0, -h / 2);
      ctx.lineTo(w / 5, h / 6);
      ctx.lineTo(0, 0);
      ctx.lineTo(-w / 5, h / 6);
      ctx.closePath();
      if (symbol?.halo.enabled) {
        ctx.lineJoin = 'round';
        ctx.strokeStyle = symbol.halo.outlineColor;
        ctx.lineWidth = Math.max(1, symbol.halo.sizePt * 2 * (h / 80));
        ctx.stroke();
        ctx.strokeStyle = symbol.halo.color;
        ctx.lineWidth = Math.max(1, symbol.halo.sizePt * (h / 80));
        ctx.stroke();
      }
      if (hasFill) {
        ctx.fillStyle = ink;
        ctx.fill();
        ctx.font = `${Math.max(10, h / 5)}px ${tokens.font.family.ui}`;
        ctx.textAlign = 'center';
        ctx.fillText('N', 0, h / 2);
      }
      ctx.restore();
      continue;
    }
    if (el.type === 'scaleBar') {
      if (!el.visible) continue;
      const frame = layout.elements.find(
        (item) => item.type === 'mapFrame' && item.id === el.mapFrameId,
      );
      const denom = frame && frame.type === 'mapFrame'
        ? effectiveScaleBarDenominator(el, frame.camera)
        : null;
      const meta = scaleBarStyleMeta(el.styleId);
      const geometry = scaleBarElementLayout(el, denom);
      if (!geometry) {
        ctx.font = `${Math.max(10, h / 3)}px ${tokens.font.family.ui}`;
        ctx.fillText('Scale unavailable', x, y + h * 0.6);
        continue;
      }
      const offsetX = mm(ptToMm(el.horizontalOffsetPt));
      const offsetY = mm(ptToMm(el.verticalOffsetPt));
      const textSymbol: ScaleBarTextSymbol = {
        ...DEFAULT_SCALE_BAR_TEXT_SYMBOL,
        ...el.textSymbol,
        halo: {
          ...DEFAULT_SCALE_BAR_TEXT_SYMBOL.halo,
          ...el.textSymbol?.halo,
        },
        shadow: {
          ...DEFAULT_SCALE_BAR_TEXT_SYMBOL.shadow,
          ...el.textSymbol?.shadow,
        },
        callout: {
          ...DEFAULT_SCALE_BAR_TEXT_SYMBOL.callout,
          ...el.textSymbol?.callout,
        },
      };
      const dual = geometry.lower != null;
      const barHeight = Math.max(3, h * (dual ? 0.1 : 0.22));
      drawScaleBarRow(
        ctx,
        geometry.upper,
        meta.family,
        x + offsetX,
        y + h * (dual ? 0.28 : 0.45) + offsetY,
        barHeight,
        mm,
        textSymbol,
        dpi,
        true,
      );
      if (geometry.lower) {
        drawScaleBarRow(
          ctx,
          geometry.lower,
          meta.family,
          x + offsetX,
          y + h * 0.62 + offsetY,
          barHeight,
          mm,
          textSymbol,
          dpi,
          false,
        );
      }
      continue;
    }
    if (el.type === 'legend') {
      if (!el.visible) continue;
      const style = legendStyleMeta(el.styleId ?? 'centerTitle');
      const background = el.background;
      const border = el.border;
      const shadow = el.shadow;
      if (shadow?.enabled) {
        ctx.fillStyle = shadow.color;
        ctx.fillRect(
          x + mm(ptToMm(shadow.offsetXPt)),
          y + mm(ptToMm(shadow.offsetYPt)),
          w,
          h,
        );
      }
      if (background?.enabled ?? style.background.enabled) {
        ctx.fillStyle = background?.color ?? style.background.color;
        ctx.fillRect(x, y, w, h);
      }
      if (border?.enabled ?? style.border.enabled) {
        ctx.strokeStyle = border?.color ?? style.border.color;
        ctx.lineWidth = Math.max(
          1,
          ((border?.outlineWidthPt ?? 1) * dpi) / 72,
        );
        ctx.strokeRect(x, y, w, h);
      }
      const allItems = legendItemsForLayers(
        options.legendLayers,
        options.visibleLayerIds,
        el.layerIds,
      ).filter((item) => el.itemOverrides?.[item.id]?.visible !== false);
      const columns =
        el.fittingStrategy === 'adjustColumns'
        || el.fittingStrategy === 'adjustColumnsAndFont'
          ? Math.max(1, Math.min(4, Math.ceil(allItems.length / 4)))
          : Math.max(1, el.columnCount ?? style.columnCount);
      const paddingX = mm(ptToMm(
        Math.max(background?.gapXPt ?? 4, border?.gapXPt ?? 4),
      ));
      const paddingY = mm(ptToMm(
        Math.max(background?.gapYPt ?? 4, border?.gapYPt ?? 4),
      ));
      const columnGap = mm(ptToMm(el.spacing?.columnGapPt ?? 12));
      const columnWidth = (w - paddingX * 2 - columnGap * (columns - 1)) / columns;
      const titleHeight = el.showTitle === false
        ? 0
        : (legendTextSymbol(el, 'title').sizePt * dpi) / 72
          + mm(ptToMm(el.spacing?.titleGapPt ?? 5));
      if (el.showTitle !== false) {
        drawScaleText(
          ctx,
          el.title ?? 'Legend',
          x + paddingX,
          y + paddingY + (legendTextSymbol(el, 'title').sizePt * dpi) / 72,
          legendTextSymbol(el, 'title'),
          dpi,
          'left',
        );
      }
      const rowsPerColumn = Math.max(1, Math.ceil(allItems.length / columns));
      allItems.forEach((item, itemIndex) => {
        const override = el.itemOverrides?.[item.id] ?? {};
        const label = override?.label ?? item.label;
        const column =
          el.fittingStrategy === 'manualColumns'
            ? Math.min(columns - 1, Math.max(0, (override.manualColumn ?? 1) - 1))
            : Math.floor(itemIndex / rowsPerColumn);
        const rowIndex =
          el.fittingStrategy === 'manualColumns'
            ? allItems
                .slice(0, itemIndex)
                .filter(
                  (prior) =>
                    (el.itemOverrides?.[prior.id]?.manualColumn ?? 1)
                    === column + 1,
                ).length
            : itemIndex % rowsPerColumn;
        const itemHeight = Math.max(
          mm(ptToMm(override.patchHeightPt ?? 12)),
          (legendTextSymbol(el, 'label').sizePt * dpi) / 72,
        ) + mm(ptToMm(el.spacing?.classGapPt ?? 2)) + 18;
        const itemX = x + paddingX + column * (columnWidth + columnGap);
        let itemY = y + paddingY + titleHeight + rowIndex * itemHeight;
        const showLayerName = override.showLayerName ?? style.showLayerName;
        if (showLayerName) {
          drawScaleText(
            ctx,
            label,
            itemX + mm(ptToMm(override.layerNameIndentPt ?? 2)),
            itemY + (legendTextSymbol(el, 'layerName').sizePt * dpi) / 72,
            legendTextSymbol(el, 'layerName'),
            dpi,
            'left',
          );
          itemY +=
            (legendTextSymbol(el, 'layerName').sizePt * dpi) / 72
            + mm(ptToMm(el.spacing?.layerNameGapPt ?? 2));
        }
        const patchW = mm(ptToMm(override.patchWidthPt ?? 24 * style.patchScale));
        const patchH = mm(ptToMm(override.patchHeightPt ?? 12 * style.patchScale));
        const rampX = itemX + mm(ptToMm(override.classIndentPt ?? 0));
        const rampY = itemY;
        const colors = item.rasterRamp?.cssGradient.match(/#[0-9a-f]{3,8}/gi)
          ?? [tokens.color.ink.muted];
        if (item.rasterRamp) {
          const horizontal = override.type === 'horizontal';
          colors.forEach((_, colorIndex) => {
            const sourceIndex = override.reverseRamp
              ? colors.length - colorIndex - 1
              : colorIndex;
            ctx.fillStyle = colors[sourceIndex];
            if (horizontal) {
              const stripW = patchW / colors.length;
              ctx.fillRect(
                rampX + colorIndex * stripW,
                rampY,
                stripW + 1,
                patchH,
              );
            } else {
              const stripH = patchH / colors.length;
              ctx.fillRect(
                rampX,
                rampY + patchH - (colorIndex + 1) * stripH,
                patchW,
                stripH + 1,
              );
            }
          });
        } else {
          ctx.fillStyle = item.patchColor ?? tokens.color.accent.strong;
          ctx.fillRect(rampX, rampY, patchW, patchH);
        }
        ctx.strokeStyle = tokens.color.ink.inverse;
        ctx.strokeRect(rampX, rampY, patchW, patchH);
        if (override.showLabel !== false) {
          drawScaleText(
            ctx,
            `${label}${
              override.showFeatureCount && item.featureCount != null
                ? ` (${item.featureCount})`
                : ''
            }`,
            rampX + patchW + mm(ptToMm(el.spacing?.patchLabelGapPt ?? 4)),
            rampY + Math.max(patchH * 0.7, 8),
            legendTextSymbol(el, 'label'),
            dpi,
            'left',
          );
        }
        if (item.rasterRamp) {
          drawScaleText(
            ctx,
            override.maxLabel ?? item.rasterRamp.maxLabel,
            rampX + patchW + 4,
            rampY + 8,
            legendTextSymbol(el, 'label'),
            dpi,
            'left',
          );
          drawScaleText(
            ctx,
            override.minLabel ?? item.rasterRamp.minLabel,
            rampX + patchW + 4,
            rampY + patchH,
            legendTextSymbol(el, 'label'),
            dpi,
            'left',
          );
        }
      });
      continue;
    }
    if (el.type === 'graphic') {
      renderLayoutGraphicToCanvas(
        ctx,
        el,
        { x, y, width: w, height: h },
        dpi,
      );
      continue;
    }
    if (el.type === 'picture') {
      if (!el.visible) continue;
      ctx.save();
      ctx.translate(x + w / 2, y + h / 2);
      ctx.rotate((el.rotation * Math.PI) / 180);
      ctx.scale(el.flipX ? -1 : 1, el.flipY ? -1 : 1);
      ctx.globalAlpha = el.opacityPercent / 100;
      if (el.shadow.enabled) {
        ctx.fillStyle = el.shadow.color;
        ctx.fillRect(
          -w / 2 + mm(ptToMm(el.shadow.offsetXPt)),
          -h / 2 + mm(ptToMm(el.shadow.offsetYPt)),
          w,
          h,
        );
      }
      if (el.background.enabled) {
        ctx.fillStyle = el.background.color;
        ctx.fillRect(-w / 2, -h / 2, w, h);
      }
      const gapX = mm(ptToMm(Math.max(el.background.gapXPt, el.border.gapXPt)));
      const gapY = mm(ptToMm(Math.max(el.background.gapYPt, el.border.gapYPt)));
      const image = pictures.get(el.id);
      if (image) {
        ctx.drawImage(image, -w / 2 + gapX, -h / 2 + gapY, w - gapX * 2, h - gapY * 2);
      }
      if (el.border.enabled && el.border.outlineWidthPt > 0) {
        ctx.strokeStyle = el.border.outlineColor;
        ctx.lineWidth = Math.max(1, (el.border.outlineWidthPt * dpi) / 72);
        ctx.strokeRect(-w / 2, -h / 2, w, h);
      }
      ctx.restore();
      continue;
    }
    if (el.type === 'tableFrame') {
      if (!el.visible) continue;
      const data = el.sourceLayerId
        ? options.tableDataByLayer?.[el.sourceLayerId] ?? null
        : null;
      const fields = el.fields.filter((field) => field.visible);
      const rows = (data?.rows ?? []).slice(0, el.maximumRows);
      const paddingX = mm(ptToMm(el.frame.gapXPt));
      const paddingY = mm(ptToMm(el.frame.gapYPt));
      if (el.frame.enabled) {
        ctx.fillStyle = el.frame.color;
        ctx.fillRect(x, y, w, h);
      }
      if (el.frame.outlineWidthPt > 0) {
        ctx.strokeStyle = el.frame.outlineColor;
        ctx.lineWidth = Math.max(1, (el.frame.outlineWidthPt * dpi) / 72);
        ctx.strokeRect(x, y, w, h);
      }
      if (fields.length === 0 || rows.length === 0) {
        drawScaleText(
          ctx,
          data?.loading ? 'Loading table…' : 'No records',
          x + paddingX,
          y + paddingY + 12,
          el.rowTextSymbol,
          dpi,
          'left',
        );
        continue;
      }
      const headingHeight = el.showHeading
        ? Math.max(16, (el.headingTextSymbol.sizePt * dpi) / 72 + 8)
        : 0;
      const rowHeight = Math.max(14, (el.rowTextSymbol.sizePt * dpi) / 72 + 6);
      const rowsPerColumn = Math.max(
        1,
        Math.floor((h - paddingY * 2 - headingHeight) / rowHeight),
      );
      const frameColumns =
        el.fittingStrategy === 'adjustColumns'
        || el.fittingStrategy === 'adjustColumnsAndFont'
          ? Math.max(1, Math.min(4, Math.ceil(rows.length / rowsPerColumn)))
          : 1;
      const columnGap = mm(ptToMm(el.frameColumnGapPt));
      const frameColumnWidth =
        (w - paddingX * 2 - columnGap * (frameColumns - 1)) / frameColumns;
      const cellWidth = frameColumnWidth / fields.length;
      for (let frameColumn = 0; frameColumn < frameColumns; frameColumn += 1) {
        const frameX =
          x + paddingX + frameColumn * (frameColumnWidth + columnGap);
        const chunk = rows.slice(
          frameColumn * rowsPerColumn,
          (frameColumn + 1) * rowsPerColumn,
        );
        if (el.showHeading) {
          fields.forEach((field, fieldIndex) => {
            const cellX = frameX + fieldIndex * cellWidth;
            ctx.fillStyle = el.headingBackgroundColor;
            ctx.fillRect(cellX, y + paddingY, cellWidth, headingHeight);
            ctx.strokeStyle = el.headingBorderColor;
            ctx.strokeRect(cellX, y + paddingY, cellWidth, headingHeight);
            drawScaleText(
              ctx,
              field.alias,
              cellX + 4,
              y + paddingY + headingHeight * 0.68,
              el.headingTextSymbol,
              dpi,
              field.headingAlignment,
            );
          });
          ctx.strokeStyle = el.headingUnderlineColor;
          ctx.beginPath();
          ctx.moveTo(frameX, y + paddingY + headingHeight);
          ctx.lineTo(frameX + frameColumnWidth, y + paddingY + headingHeight);
          ctx.stroke();
        }
        chunk.forEach((row, rowIndex) => {
          const cycle =
            Math.max(1, el.rowBackground1Count)
            + Math.max(1, el.rowBackground2Count);
          const first =
            (rowIndex + frameColumn * rowsPerColumn) % cycle
            < Math.max(1, el.rowBackground1Count);
          const rowY = y + paddingY + headingHeight + rowIndex * rowHeight;
          fields.forEach((field, fieldIndex) => {
            const cellX = frameX + fieldIndex * cellWidth;
            ctx.fillStyle = first ? el.rowBackground1 : el.rowBackground2;
            ctx.fillRect(cellX, rowY, cellWidth, rowHeight);
            ctx.strokeStyle = el.rowBorderColor;
            ctx.strokeRect(cellX, rowY, cellWidth, rowHeight);
            drawScaleText(
              ctx,
              String(row[field.name] ?? ''),
              cellX + 4,
              rowY + rowHeight * 0.7,
              el.rowTextSymbol,
              dpi,
              field.alignment,
            );
          });
        });
      }
    }
  }

  return canvas;
}

export async function exportLayoutPng(
  layout: LayoutDocument,
  options: ExportLayoutOptions,
): Promise<boolean> {
  const canvas = await rasterizeLayout(layout, options);
  if (!canvas) return false;
  const link = window.document.createElement('a');
  link.download = `geoviewer-layout-${Date.now()}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
  return true;
}

export async function printLayoutPage(
  layout: LayoutDocument,
  options: ExportLayoutOptions,
): Promise<boolean> {
  const canvas = await rasterizeLayout(layout, options);
  if (!canvas) return false;
  const popup = window.open('', '_blank');
  if (!popup) return false;
  popup.document.write(
    `<html><head><title>Layout</title></head><body style="margin:0"><img alt="Layout" src="${canvas.toDataURL('image/png')}" style="width:100%"/></body></html>`,
  );
  popup.document.close();
  popup.focus();
  popup.print();
  return true;
}
